// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
//
// The review screen's shell: data, selection and the wiring between the two
// columns. The left column is `./AskV2ReviewLeftColumn.tsx`, the artwork
// column's viewers are `./AskV2ReviewCompareLayout.tsx`, the comment-chip
// effects are `./useAskV2ReviewCommentContext.ts` and confirm/reject is
// `./useAskV2ReviewJudge.ts`. Add new behavior there, not here. The pin
// grouping (spec dave.savostyanov/0090) lives in `./pinGroups.ts` and
// `./useAskV2ReviewPinView.ts`; this module only wires them.
import { CircleNotch } from "@phosphor-icons/react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewArtworkHeader } from "./AskV2ReviewArtworkHeader";
import { AskV2ReviewCompareLayout } from "./AskV2ReviewCompareLayout";
import {
  AskV2ReviewDraftPinsNote,
  draftPinTone,
} from "./AskV2ReviewDraftPinsNote";
import { AskV2ReviewLeftColumn } from "./AskV2ReviewLeftColumn";
import { AskV2ReviewPanes } from "./AskV2ReviewPanes";
import { AskV2ReviewPaneTabs } from "./AskV2ReviewPaneTabs";
import { AskV2ReviewPinGroupBar } from "./AskV2ReviewPinGroupBar";
import { AskV2ReviewPinViewControls } from "./AskV2ReviewPinViewControls";
import { clusterCounts, groupMark, severityPinTone } from "./pinGroups";
import {
  type CommentWithOptionalTags,
  savedReviewCommentFilter,
} from "./reviewCommentTags";
import {
  useAskPunttReview,
  useCreateAskPunttReviewComment,
} from "./useAskPunttReview";
import { useAskV2ReviewCommentContext } from "./useAskV2ReviewCommentContext";
import { useAskV2ReviewCommentFilter } from "./useAskV2ReviewCommentFilter";
import { useAskV2ReviewJudge } from "./useAskV2ReviewJudge";
import { useAskV2ReviewPanelComments } from "./useAskV2ReviewPanelComments";
import { useAskV2ReviewPinView } from "./useAskV2ReviewPinView";
import { useAskV2ReviewVersionCompare } from "./useAskV2ReviewVersionCompare";
import { useAskV2ReviewWorkspace } from "./useAskV2ReviewWorkspace";
import { comparePinTone } from "./versionChanges";
import { type ReviewVersionOption } from "./VersionPicker";

import {
  type AskPunttArtifactContextValue,
  type HumanCommentSubmitInput,
  useReviewArtworkFocus,
} from "../../components/ask-puntt-review";
import { mapReviewFilesToAssets } from "../../components/ask-puntt-review/mapReviewAssets";
import { type AskV2TransportApi } from "../transport/useAskV2Transport";

type FeedMember = {
  _id: string;
  name?: string;
  email?: string;
  avatar?: string | null;
};

type AskV2ReviewWorkspaceProps = {
  workspace: ReturnType<typeof useAskV2ReviewWorkspace>;
  transport: AskV2TransportApi;
  versionOptions?: readonly ReviewVersionOption[];
  feedPresentation?: "chat" | "feed";
  feedActorName?: string;
  feedActorAvatar?: string;
  feedMembers?: FeedMember[];
  currentUserId?: string;
  authorNameByUserId?: Readonly<Record<string, string>>;
  artifactContext?: AskPunttArtifactContextValue;
  /** Chat composer pinned under the left findings column only. */
  composer?: ReactNode;
  /**
   * The viewer may not write to the chat behind this review. The transport the
   * page hands us is already gated, but three of this workspace's writes never
   * ride `AskV2TransportApi`, so the gate cannot reach them and each needs the
   * prop: the feed thread's per-row thumbs (`submitFeedback`), the artwork
   * pane's human-comment composer (`handleSubmitHumanComment` →
   * `createReviewComment`) and the findings-list judge controls
   * (`handleJudgeComment` → `updateReviewComment`). All three are withheld
   * here, and so is Edit on the viewer's own comments (the left column's
   * `handleEditComment`), which christo does gate: only a human comment's
   * author may change its text.
   *
   * Defence in depth, not authorization: christo authorizes the two review
   * comment routes on enterprise scope alone (see the scope note on
   * `canInteract` in AskV2ChatPage.tsx), so those POSTs would still succeed if
   * a caller reached them another way. Making the read-only posture real needs
   * a membership check inside `createReviewComment` / `updateReviewComment`.
   */
  readOnly?: boolean;
};

// why: one identity while the review loads, so the filter's memos do not
// recompute on every render of a workspace that has no comments yet.
const EMPTY_COMMENTS: readonly CommentWithOptionalTags[] = [];

export function AskV2ReviewWorkspace({
  workspace,
  transport,
  versionOptions = [],
  feedPresentation = "chat",
  feedActorName,
  feedActorAvatar,
  feedMembers,
  currentUserId,
  authorNameByUserId,
  artifactContext,
  composer,
  readOnly = false,
}: AskV2ReviewWorkspaceProps) {
  const { t } = useTranslation("ui");
  const reviewQuery = useAskPunttReview(workspace.reviewId);
  const review = reviewQuery.data?.review;
  const artworkFiles = useMemo(
    () => mapReviewFilesToAssets(review?.files ?? []),
    [review?.files],
  );
  const contextFiles = useMemo(
    () => mapReviewFilesToAssets(review?.contextAssets ?? []),
    [review?.contextAssets],
  );

  // why: syncing comment selection to the /comments/:id route remounts the page
  // and resets the findings list scroll position; keep selection local while open.
  const artworkFocus = useReviewArtworkFocus({
    comments: review?.comments,
    initialCommentId: workspace.commentId,
  });

  const commentFilter = useAskV2ReviewCommentFilter({
    comments: review?.comments ?? EMPTY_COMMENTS,
    authorNameByUserId,
    reviewId: review?.reviewId,
    viewerUserId: currentUserId,
    startedBy: review?.startedBy,
    selectedReviewerIds: review?.selectedReviewerIds,
    selectedExpertIds: review?.selectedExpertIds,
    savedFilter: savedReviewCommentFilter(review?.commentFilter),
  });
  const { isVisible: isCommentFilterVisible } = commentFilter;

  const { panelComments, loadedGroups } = useAskV2ReviewPanelComments({
    comments: review?.comments,
    reviewId: review?.reviewId,
    isCommentFilterVisible,
  });

  const {
    selectedCommentId,
    selectComment,
    clearComment,
    artworkPaneRef,
    artworkHeaderControls,
    setArtworkHeaderControls,
  } = artworkFocus;

  // The pins as she chose to see them: all, or the open findings in groups,
  // minor and check findings shown or not (spec dave.savostyanov/0090).
  const pinView = useAskV2ReviewPinView({
    comments: panelComments,
    loadedGroups,
    summaries: review?.groupSummaries,
    selectedCommentId,
  });

  // why: numbered once over the findings the list shows, in its order (group by
  // group when grouped), and handed to the list and the pins alike, so a card
  // and its pin carry one number.
  const commentNumbers = useMemo(() => {
    const numbers: Record<string, number> = {};
    pinView.ordered.forEach((comment, index) => {
      numbers[comment._id] = index + 1;
    });
    return numbers;
  }, [pinView.ordered]);
  // A finding the minor switch hides cannot stay selected; a companion stays, inside its lead's card.
  const shownComments = useMemo(
    () => [
      ...pinView.ordered,
      ...Array.from(pinView.companionsOf.values()).flat(),
    ],
    [pinView.companionsOf, pinView.ordered],
  );

  const { mutateAsync: createReviewComment } = useCreateAskPunttReviewComment(
    workspace.reviewId,
  );
  const [pendingFocusCommentId, setPendingFocusCommentId] = useState<
    string | null
  >(null);
  const { setLeftPane } = workspace;
  const { removeCommentContext } = transport;
  const handleRemoveCommentContext = useCallback(
    (id: string) => {
      removeCommentContext(id);
      clearComment();
    },
    [clearComment, removeCommentContext],
  );
  const handleSubmitHumanComment = useCallback(
    async (input: HumanCommentSubmitInput) => {
      const { comment } = await createReviewComment({
        description: input.description,
        rel_x: input.relX,
        rel_y: input.relY,
        page_num: input.page,
      });
      setPendingFocusCommentId(comment._id);
      setLeftPane("comments");
    },
    [createReviewComment, setLeftPane],
  );

  // why: the saved comment reaches `review.comments` one render after the
  // mutation resolves; selecting it synchronously misses the focus lookup.
  useEffect(() => {
    if (pendingFocusCommentId == null) return;
    if (!review?.comments.some((c) => c._id === pendingFocusCommentId)) {
      return;
    }
    selectComment(pendingFocusCommentId);
    setPendingFocusCommentId(null);
  }, [pendingFocusCommentId, review?.comments, selectComment]);

  useAskV2ReviewCommentContext({
    review,
    workspace,
    transport,
    selectedCommentId,
    panelComments: shownComments,
    commentNumbers,
    isCommentFilterVisible,
    clearComment,
  });

  const { judgmentFailed, isJudgmentPending, handleJudgeComment } =
    useAskV2ReviewJudge({
      reviewId: workspace.reviewId,
      panelComments: pinView.ordered,
      selectComment,
      clearComment,
    });

  const resolvedVersionOptions = useMemo(() => {
    if (versionOptions.length > 0) return versionOptions;
    if (!review) return [];
    return [{ reviewId: review.reviewId, title: review.title }];
  }, [review, versionOptions]);

  // The mark a review of this artwork reads under ("V2"), as the comparison
  // names its sides; none for a lineage of one, which has no other version.
  const versionMarkOf = useCallback(
    (id: string) => {
      if (resolvedVersionOptions.length < 2) return undefined;
      const at = resolvedVersionOptions.findIndex(
        (option) => option.reviewId === id,
      );
      if (at < 0) return undefined;
      return `V${resolvedVersionOptions[at].versionNumber ?? at + 1}`;
    },
    [resolvedVersionOptions],
  );

  // SPIKE (side-by-side version compare): another review of this chat shown
  // beside this one. The findings list steps aside while it is open.
  const compare = useAskV2ReviewVersionCompare({
    review,
    versionOptions: resolvedVersionOptions,
    selectedCommentId,
    historyMessages: transport.historyMessages ?? [],
    panelComments: pinView.ordered,
    commentNumbers,
  });
  const { isComparing } = compare;

  // The artwork follows the grouping only outside a comparison, which draws its own pins.
  const groupedPins = pinView.grouping && !isComparing;
  // The version beside this one hides, folds and numbers its findings the way
  // this review's pin view does.
  const comparedPinView = useMemo(
    () => ({
      enabled: pinView.enabled,
      showMinor: pinView.showMinor,
      grouping: pinView.grouping,
      groupBy: pinView.groupBy,
    }),
    [pinView.enabled, pinView.groupBy, pinView.grouping, pinView.showMinor],
  );
  // A closed group's pin is drawn numbered so it takes the pill path, which prints the group's letter; open, each finding its own number.
  const groupPinNumbers = useMemo(() => {
    if (pinView.openGroup) return commentNumbers;
    const numbers: Record<string, number> = { ...commentNumbers };
    pinView.groups.forEach((group, index) => {
      numbers[group.hub._id] = index + 1;
    });
    return numbers;
  }, [commentNumbers, pinView.groups, pinView.openGroup]);
  // Every closed group, one finding or many, is drawn as a pill with its letter counting its findings to fix and its minor ones.
  const groupPinCluster = useCallback(
    (comment: { _id: string }) => {
      if (!groupedPins || pinView.openGroup) return null;
      const index = pinView.groups.findIndex((g) => g.hub._id === comment._id);
      const group = index >= 0 ? pinView.groups[index] : null;
      if (!group) return null;
      const counts = clusterCounts(group);
      const mark = groupMark(index);
      return {
        ...counts,
        mark,
        label: t("askV2.review.pinCluster.label", {
          mark,
          critical: counts.critical,
          minor: counts.minor,
        }),
      };
    },
    [groupedPins, pinView.groups, pinView.openGroup, t],
  );
  const toggleGroup = useCallback(
    (key: string) => {
      if (pinView.openGroupKey !== key) {
        pinView.setOpenGroupKey(key);
        return;
      }
      // why: the open finding would reopen the group it sits in; closing the group closes it too.
      if (
        selectedCommentId != null &&
        pinView.groupOf(selectedCommentId)?.key === key
      ) {
        clearComment();
      }
      pinView.setOpenGroupKey(null);
    },
    [clearComment, pinView, selectedCommentId],
  );
  const isReviewing = review?.aiRunning === true;
  // The group the artwork's bar is open on: only outside a comparison, which draws its own pins.
  const barGroup = groupedPins ? pinView.openGroup : null;
  // A link from the chat's version check card opens the comparison. why wait
  // for canCompare: the versions list loads after the review, and taking the
  // request before it does would drop it with no comparison to open.
  const { compareRequested, consumeCompareRequest } = workspace;
  const { canCompare, setComparing } = compare;
  useEffect(() => {
    if (!compareRequested || !canCompare) return;
    setComparing(true);
    consumeCompareRequest();
  }, [canCompare, compareRequested, consumeCompareRequest, setComparing]);
  const headerControls = (
    <div
      className="ml-auto flex items-center gap-2"
      data-testid="ask-v2-review-header-controls"
    >
      {artworkHeaderControls}
    </div>
  );

  const paneTabs = (
    <AskV2ReviewPaneTabs
      data-testid="ask-v2-review-pane-tabs"
      leftPane={workspace.leftPane}
      // why: the badge counts every finding the list draws and the pins
      // show. Counting `review.comments` instead let the tab read
      // "Comments (3)" beside a list of 2 after a reject.
      commentCount={panelComments.length}
      onChange={workspace.setLeftPane}
    />
  );

  if (reviewQuery.isLoading) {
    return (
      <div
        className="flex flex-1 items-center justify-center"
        data-testid="ask-v2-review-loading"
      >
        <CircleNotch
          size={24}
          weight="bold"
          className="animate-spin text-puntt-neutral-gray-9"
        />
      </div>
    );
  }

  if (reviewQuery.isError || !review) {
    return (
      <div
        className="flex flex-1 items-center justify-center px-6 text-center text-sm text-puntt-neutral-gray-10"
        data-testid="ask-v2-review-error"
      >
        {t("askV2.review.loadError")}
      </div>
    );
  }

  return (
    <div
      className="flex size-full min-h-0 flex-1 overflow-hidden"
      data-testid="ask-v2-review-workspace"
      // why: a `<div>` with no role is `role="generic"`, and ARIA prohibits
      // `aria-label` there (axe `aria-prohibited-attr`) -- the name was
      // computed and then dropped, so both halves of this screen reached a
      // reader as unnamed generic boxes and the artwork pane below could not
      // be told apart from the findings column. The two names this component
      // already pays for only exist once something owns them; `region` is what
      // the findings list itself uses (`<section aria-label>` in
      // AskV2ReviewCommentsList) and what CommentLoginWall/TimeSavedBubble use
      // for the same job.
      role="region"
      aria-label={t("askV2.review.workspaceLabel")}
    >
      <AskV2ReviewPanes
        abortSignal={review.reviewId}
        left={
          <AskV2ReviewLeftColumn
            review={review}
            workspace={workspace}
            transport={transport}
            compare={compare}
            commentFilter={commentFilter}
            panelComments={panelComments}
            listComments={pinView.ordered}
            commentNumbers={commentNumbers}
            versionMark={versionMarkOf}
            listView={{
              companionsOf: pinView.companionsOf,
              headerControls: pinView.enabled ? (
                <AskV2ReviewPinViewControls
                  mode={pinView.mode}
                  groupBy={pinView.groupBy}
                  showMinor={pinView.showMinor}
                  pinCount={pinView.ordered.length}
                  groupCount={pinView.groupCount}
                  onModeChange={pinView.setMode}
                  onGroupByChange={pinView.setGroupBy}
                  onShowMinorChange={pinView.setShowMinor}
                />
              ) : null,
              severityBands: pinView.enabled && !pinView.grouping,
              pinGroups: pinView.grouping
                ? {
                    groups: pinView.groups,
                    openGroupKey: pinView.openGroupKey,
                    onToggleGroup: toggleGroup,
                  }
                : undefined,
            }}
            artworkFiles={artworkFiles}
            contextFiles={contextFiles}
            artworkPaneRef={artworkPaneRef}
            selectedCommentId={selectedCommentId}
            selectComment={selectComment}
            clearComment={clearComment}
            onRemoveCommentContext={handleRemoveCommentContext}
            judgmentFailed={judgmentFailed}
            judgmentPending={isJudgmentPending}
            onJudge={handleJudgeComment}
            feedPresentation={feedPresentation}
            feedActorName={feedActorName}
            feedActorAvatar={feedActorAvatar}
            feedMembers={feedMembers}
            currentUserId={currentUserId}
            authorNameByUserId={authorNameByUserId}
            artifactContext={artifactContext}
            composer={composer}
            readOnly={readOnly}
            onBack={workspace.closeReview}
          />
        }
        right={
          <div
            className="flex min-h-0 min-w-0 flex-[3] flex-col overflow-hidden bg-puntt-neutral-gray-2"
            data-testid="ask-v2-review-artwork"
            // why: same prohibited-attribute drop as the workspace wrapper --
            // "Artwork" is the only thing telling this pane apart from the
            // findings column, and a bare `<div>` may not carry a name.
            role="region"
            aria-label={t("askV2.review.artworkLabel")}
          >
            <AskV2ReviewArtworkHeader
              review={review}
              compare={compare}
              versionOptions={resolvedVersionOptions}
              onSwitchReview={workspace.switchReview}
              historyMessages={transport.historyMessages}
              paneTabs={paneTabs}
              viewerControls={headerControls}
            />
            <AskV2ReviewCompareLayout
              review={review}
              compare={compare}
              artworkFiles={artworkFiles}
              artworkPaneRef={artworkPaneRef}
              setArtworkHeaderControls={setArtworkHeaderControls}
              selectedCommentId={selectedCommentId}
              commentNumbers={commentNumbers}
              currentUserId={currentUserId}
              comparedPinView={comparedPinView}
              openPins={{
                comments: groupedPins
                  ? pinView.viewerComments
                  : compare.viewerComments,
                numbers: groupedPins ? groupPinNumbers : compare.viewerNumbers,
                activeId: pinView.pinOf(
                  compare.steppedPin?._id ?? selectedCommentId,
                ),
                tone: isComparing
                  ? comparePinTone(compare.openSide)
                  : isReviewing
                    ? draftPinTone
                    : severityPinTone,
                cluster: groupPinCluster,
              }}
              overlay={
                <>
                  {barGroup ? (
                    <AskV2ReviewPinGroupBar
                      group={barGroup}
                      onBack={() => toggleGroup(barGroup.key)}
                      onStep={(direction) => {
                        workspace.setLeftPane("comments");
                        selectComment(
                          pinView.stepFinding(
                            direction,
                            selectedCommentId,
                            barGroup,
                          ),
                        );
                      }}
                    />
                  ) : null}
                  {isComparing ? null : (
                    <AskV2ReviewDraftPinsNote
                      running={isReviewing}
                      count={review.comments.length}
                    />
                  )}
                </>
              }
              onOpenComment={(commentId) => {
                // A closed group's pin opens the group, not its first finding.
                const hub =
                  groupedPins && !pinView.openGroup
                    ? pinView.hubGroup(commentId)
                    : null;
                workspace.setLeftPane("comments");
                if (hub && hub.members.length > 1) {
                  pinView.setOpenGroupKey(hub.key);
                  return;
                }
                selectComment(commentId);
              }}
              onArtworkBackgroundClick={() => {
                // Stryker disable next-line ConditionalExpression: here clearComment only sets the local selection to null (no deep-link callback is passed), so clearing an empty selection changes nothing; forcing it false is killed by "clears the selected finding and its chip when the artwork background is clicked"
                if (selectedCommentId != null) {
                  clearComment();
                } else if (barGroup) {
                  pinView.setOpenGroupKey(null);
                }
              }}
              onSubmitHumanComment={
                readOnly ? undefined : handleSubmitHumanComment
              }
            />
          </div>
        }
      />
    </div>
  );
}
